import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import LoadingContainer from 'components/common/LoadingContainer';
import SoftBadge from 'components/common/SoftBadge';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { parseRouteParams, formatMoney } from 'helpers/utils';
import { useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import paths from 'routes/paths';
import { addMonths, format } from 'date-fns';
import SelectBeneficiaryModal from '../beneficiaries/sponsorships/sponsorship-form/SelectBeneficiaryModal';
import { useDonorSponsorships } from 'hooks/useDonors';
import { useEndSponsorship } from 'hooks/useBeneficiaries';

// One sponsorship card. `onEnd` is only passed for active sponsorships —
// history (ended/cancelled) cards render muted, without the End action.
const SponsorshipCard = ({ sponsorship, onView, onEnd, isEnding }) => {
  const {
    id: sponsorshipId,
    orphan,
    start_date,
    duration,
    status,
    allocated_amount,
    coverage_percentage,
    beneficiary_sponsorship_type
  } = sponsorship;
  if (!orphan) return null;

  const isActive = status === 'active';
  const name = [orphan.first_name, orphan.middle_name, orphan.last_name]
    .filter(Boolean)
    .join(' ');
  const typeName =
    beneficiary_sponsorship_type?.sponsorship_type?.type || 'Sponsorship';
  const startDate = start_date ? new Date(start_date) : null;
  const endDate = startDate && duration ? addMonths(startDate, duration) : null;

  return (
    <Card className={`mb-3 ${isActive ? '' : 'bg-light'}`}>
      <Card.Body className={isActive ? '' : 'py-2'}>
        <div className="d-md-flex justify-content-between">
          <div className="d-flex align-items-center mt-2 mt-md-0">
            <Avatar
              src={orphan.image}
              name={name}
              size={isActive ? '3xl' : '2xl'}
            />
            <div className="ms-3">
              <Flex alignItems="center" className="mb-1">
                <p
                  className={`fw-medium mb-0 me-2 ${
                    isActive ? 'text-900' : 'text-700'
                  }`}
                >
                  {name}
                </p>
                {orphan.simple_id && (
                  <span className="text-500 fs--1">#{orphan.simple_id}</span>
                )}
              </Flex>
              <p className="mb-2 fs--1 text-600">
                {typeName}
                {coverage_percentage !== null &&
                  coverage_percentage !== undefined && (
                    <SoftBadge
                      pill
                      bg={
                        !isActive
                          ? 'secondary'
                          : coverage_percentage >= 100
                          ? 'success'
                          : 'info'
                      }
                      className="ms-2"
                    >
                      {coverage_percentage}% coverage
                    </SoftBadge>
                  )}
                <SoftBadge
                  pill
                  bg={isActive ? 'success' : 'secondary'}
                  className="ms-2 text-capitalize"
                >
                  {status}
                </SoftBadge>
              </p>
              <Flex alignItems="center" className="flex-wrap fs--2 text-600">
                {orphan.country && (
                  <span className="me-3">
                    <FontAwesomeIcon
                      icon="map-marker-alt"
                      className="me-1 text-800"
                    />
                    {orphan.country}
                  </span>
                )}
                <span className="me-3">
                  <FontAwesomeIcon
                    icon="money-bill"
                    className="me-1 text-800"
                  />
                  {formatMoney(allocated_amount || 0)}
                </span>
                {startDate && (
                  <span className="me-3">
                    <FontAwesomeIcon
                      icon="calendar-alt"
                      className="me-1 text-800"
                    />
                    {format(startDate, 'dd MMM yyyy')}
                    {endDate && ` - ${format(endDate, 'dd MMM yyyy')}`}
                  </span>
                )}
              </Flex>
            </div>
          </div>
          <Flex
            className="mt-3 mt-md-0 gap-2"
            alignItems="start"
            justifyContent="end"
          >
            <Button
              size="sm"
              variant="falcon-default"
              onClick={() => onView(orphan.id)}
            >
              View Sponsorship
            </Button>
            {onEnd && (
              <Button
                size="sm"
                variant="danger"
                disabled={isEnding}
                onClick={() => onEnd(sponsorshipId)}
              >
                <FontAwesomeIcon icon="times" className="me-1" />
                End
              </Button>
            )}
          </Flex>
        </div>
      </Card.Body>
    </Card>
  );
};

SponsorshipCard.propTypes = {
  sponsorship: PropTypes.object.isRequired,
  onView: PropTypes.func.isRequired,
  onEnd: PropTypes.func,
  isEnding: PropTypes.bool
};

const DonorSponsorships = () => {
  const { id } = useParams();
  const {
    sponsorships,
    isLoading: sponsorshipsLoading,
    refetch
  } = useDonorSponsorships(id);
  const { endSponsorship, isEnding } = useEndSponsorship();
  const navigate = useNavigate();

  const [showSelectBeneficiary, setShowSelectBeneficiary] = useState(false);
  const [endSponsorshipId, setEndSponsorshipId] = useState(null);

  const { activeSponsorships, endedSponsorships } = useMemo(() => {
    const list = sponsorships || [];
    return {
      activeSponsorships: list.filter(s => s.status === 'active'),
      endedSponsorships: list.filter(s => s.status !== 'active')
    };
  }, [sponsorships]);

  const handleSelectBeneficiarySubmit = selectedBeneficiaryId => {
    navigate(
      parseRouteParams(
        paths.createNewSponsorship,
        {},
        {
          beneficiaryId: selectedBeneficiaryId,
          donorId: id
        }
      )
    );
  };

  const handleViewSponsorship = orphanId => {
    navigate(parseRouteParams(paths.beneficiaryDonors, { id: orphanId }));
  };

  const handleConfirmEnd = () => {
    endSponsorship(endSponsorshipId, {
      onSuccess: () => {
        setEndSponsorshipId(null);
        refetch();
      }
    });
  };

  if (sponsorshipsLoading) {
    return <LoadingContainer />;
  }

  return (
    <>
      {showSelectBeneficiary && (
        <SelectBeneficiaryModal
          onHide={() => setShowSelectBeneficiary(false)}
          show={showSelectBeneficiary}
          onSubmit={handleSelectBeneficiarySubmit}
        />
      )}

      <ConfirmationModal
        show={!!endSponsorshipId}
        onHide={() => setEndSponsorshipId(null)}
        loading={isEnding}
        title="End Sponsorship"
        confirmText="End Sponsorship"
        handleConfirm={handleConfirmEnd}
        body="Are you sure you want to end this sponsorship? This action cannot be undone."
      />

      <Card className="mb-3">
        <Card.Header>
          <Flex justifyContent="between" alignItems="center">
            <h5 className="mb-0">Active Sponsorships</h5>
            <IconButton
              size="sm"
              icon="plus"
              variant="falcon-default"
              iconClassName="me-1"
              onClick={() => setShowSelectBeneficiary(true)}
            >
              Create Sponsorship
            </IconButton>
          </Flex>
        </Card.Header>
      </Card>

      {activeSponsorships.length === 0 && (
        <Card className="mb-3">
          <Card.Body>
            <div className="text-center">
              <h5 className="mb-3 text-500 fw-medium">
                No Active Sponsorships
              </h5>
              <Button
                onClick={() => setShowSelectBeneficiary(true)}
                variant="falcon-default"
              >
                Create Sponsorship
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {activeSponsorships.map(sponsorship => (
        <SponsorshipCard
          key={sponsorship.id}
          sponsorship={sponsorship}
          onView={handleViewSponsorship}
          onEnd={setEndSponsorshipId}
          isEnding={isEnding}
        />
      ))}

      {endedSponsorships.length > 0 && (
        <>
          <Card className="mb-3 mt-4">
            <Card.Header className="py-2">
              <h6 className="mb-0 text-600">
                <FontAwesomeIcon icon="clock" className="me-2" />
                Sponsorship History ({endedSponsorships.length})
              </h6>
            </Card.Header>
          </Card>
          {endedSponsorships.map(sponsorship => (
            <SponsorshipCard
              key={sponsorship.id}
              sponsorship={sponsorship}
              onView={handleViewSponsorship}
            />
          ))}
        </>
      )}
    </>
  );
};

export default DonorSponsorships;
